import { useState, useEffect, useRef } from "react";
import Pagination from "../../../components/common/Pagination";
import SearchBox from "../../../components/common/SearchBox";
import FlashcardItem from "../../../components/flashcard/FlashcardItem";
import { flashcardApi } from "../../../api/flashcardApi";
import { API_BASE_URL } from "../../../api/config/apiConfig";
import LoadingOverlay from "../../../components/common/LoadingOverlay";
import ConfirmModal from "../../../components/common/ConfirmModal";
import AlertMessage from "../../../components/common/AlertMessage";
import { useNavigate } from "react-router-dom";
import MultipleSearchSelectApi from "../../../components/common/MultipleSearchSelectApi";
import { categoryApi } from "../../../api/categoryApi";
import SidePanel from "../../../components/common/SidePanel";

export default function FlashcardService() {
    // rename to filtersSelected for clarity
    const [filtersSelected, setFiltersSelected] = useState([]);
    const [cards, setCards] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [idCardToDelete, setIdCardToDelete] = useState(null);
    const [selectedCard, setSelectedCard] = useState(null);

    // Search + Filter state
    const [searchText, setSearchText] = useState("");

    // Pagination state
    const [pageSize, setPageSize] = useState(3);
    const [pageNumber, setPageNumber] = useState(1);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [alert, setAlert] = useState({ type: "", message: "" });
    const [showImportModal, setShowImportModal] = useState(false);
    const [importFile, setImportFile] = useState(null);
    const [importCategoryId, setImportCategoryId] = useState("");
    const [importCategories, setImportCategories] = useState([]);
    const [importCategorySearch, setImportCategorySearch] = useState("");
    const [importCategoryPage, setImportCategoryPage] = useState(1);
    const [hasMoreImportCategories, setHasMoreImportCategories] = useState(false);
    const [isLoadingImportCategories, setIsLoadingImportCategories] = useState(false);
    const [isLoadingMoreImportCategories, setIsLoadingMoreImportCategories] = useState(false);
    const [showImportCategoryDropdown, setShowImportCategoryDropdown] = useState(false);
    const [isAutogenImport, setIsAutogenImport] = useState(false);
    const [isImporting, setIsImporting] = useState(false);
    const importCategoryDropdownRef = useRef(null);
    // Filtering logic

    const [totalPages, setTotalPages] = useState(1);

    // fetchCardsTable will derive filters from state when filtersArg is not provided
    const fetchCardsTable = async (searchKey = "", filtersArg = undefined) => {
        setIsLoading(true);
        try {
            const filtersObj = filtersArg ?? {
                // server expects list of category ids (adjust key name if your API expects different)
                CategoryIds: (filtersSelected || []).map(f => f.value)
            };

            const params = {
                pageNumber,
                pageSize,
                filters: filtersObj,
                isSortDesc: true,
                sortKey: "createdAt",
                seachKey: searchKey // keep as-is if API expects this field
            };
            const res = await flashcardApi.getTable(params);
            if (res?.success) {
                const items = res.data?.items || [];
                const total = res.data?.totalCount ?? items.length;
                const pSize = res.data?.pageSize ?? pageSize;
                const totalPage = Math.max(1, Math.ceil(total / pSize));

                setCards([...items]);
                setTotalPages(totalPage);
            } else {
                setCards([]);
                setTotalPages(1);
            }
        } finally {
            setIsLoading(false);
        }
    };

    // refetch when pageNumber, pageSize, searchText or filtersSelected change
    useEffect(() => {
        const t = setTimeout(() => {
            fetchCardsTable(searchText);
        }, 400);
        return () => clearTimeout(t);
    }, [pageNumber, pageSize, searchText]);

    const handleDeleteConfirmed = async () => {
        try {
            setIsLoading(true);
            await flashcardApi.remove(idCardToDelete);
        } finally {
            setConfirmDelete(false);
            setIdCardToDelete(null);
            // fetch with current search & filters
            await fetchCardsTable(searchText);
            setIsLoading(false);
        }
    };

    const handleFetchFilter = async (search) => {
        try {
            const params = {
                pageNumber: 1,
                pageSize: 5,
                filters: {},
                isSortDesc: false,
                sortKey: "",
                seachKey: search
            };
            const res = await categoryApi.getTable(params);
            const items = res?.data?.items || [];
            const filters = items.map(x => ({
                value: x.id,
                label: x.name
            }));
            return filters;
        } catch {
            return [];
        }
    };

    const navigate = useNavigate();

    const handleDownloadTemplate = async () => {
        try {
            const token = localStorage.getItem("accessToken");
            const res = await fetch(`${API_BASE_URL}/api/FlashCard/import/template`, {
                headers: token ? { Authorization: `Bearer ${token}` } : {}
            });

            if (!res.ok) {
                throw new Error("Cannot download template.");
            }

            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "flashcard-import-template.csv";
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            setAlert({ type: "danger", message: "Download template failed." });
        }
    };

    const fetchImportCategories = async ({ page = 1, search = "", append = false }) => {
        if (append) {
            setIsLoadingMoreImportCategories(true);
        } else {
            setIsLoadingImportCategories(true);
        }
        try {
            const pageSize = 20;
            const params = {
                pageNumber: page,
                pageSize,
                filters: {},
                isSortDesc: false,
                sortKey: "",
                seachKey: search
            };
            const res = await categoryApi.getTable(params);
            const items = res?.data?.items || [];
            const totalCount = res?.data?.totalCount ?? 0;
            const mapped = items.map((x) => ({
                value: x.id,
                label: x.name
            }));

            setImportCategories((prev) => {
                if (!append) return mapped;
                const existing = new Set(prev.map((x) => x.value));
                const additional = mapped.filter((x) => !existing.has(x.value));
                return [...prev, ...additional];
            });
            setImportCategoryPage(page);
            setHasMoreImportCategories(page * pageSize < totalCount);
        } catch {
            if (!append) {
                setImportCategories([]);
                setHasMoreImportCategories(false);
            }
            setAlert({ type: "danger", message: "Cannot load categories for import." });
        } finally {
            setIsLoadingImportCategories(false);
            setIsLoadingMoreImportCategories(false);
        }
    };

    const handleOpenImportModal = () => {
        setImportFile(null);
        setImportCategoryId("");
        setImportCategorySearch("");
        setImportCategoryPage(1);
        setHasMoreImportCategories(false);
        setImportCategories([]);
        setShowImportCategoryDropdown(false);
        setIsAutogenImport(false);
        setShowImportModal(true);
    };

    const handleCloseImportModal = () => {
        if (isImporting) return;
        setShowImportModal(false);
        setImportFile(null);
        setImportCategoryId("");
        setImportCategorySearch("");
        setImportCategories([]);
        setShowImportCategoryDropdown(false);
        setIsAutogenImport(false);
    };

    const loadMoreImportCategories = async () => {
        if (!hasMoreImportCategories || isLoadingMoreImportCategories || isLoadingImportCategories) {
            return;
        }
        await fetchImportCategories({
            page: importCategoryPage + 1,
            search: importCategorySearch,
            append: true
        });
    };

    useEffect(() => {
        if (!showImportModal) return;

        const tm = setTimeout(() => {
            fetchImportCategories({
                page: 1,
                search: importCategorySearch,
                append: false
            });
        }, 300);

        return () => clearTimeout(tm);
    }, [importCategorySearch, showImportModal]);

    useEffect(() => {
        if (!showImportModal) return;
        const handleClickOutside = (e) => {
            if (importCategoryDropdownRef.current && !importCategoryDropdownRef.current.contains(e.target)) {
                setShowImportCategoryDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [showImportModal]);

    const handleImportCards = async () => {
        if (!importFile) {
            setAlert({ type: "warning", message: "Please choose a CSV file first." });
            return;
        }
        if (!importCategoryId) {
            setAlert({ type: "warning", message: "Please choose a category for imported cards." });
            return;
        }
        const formData = new FormData();
        formData.append("file", importFile);
        formData.append("categoryId", importCategoryId);
        formData.append("isAutogen", String(isAutogenImport));

        setIsImporting(true);
        try {
            const rs = await flashcardApi.importByFile(formData);
            const errors = rs?.errors || [];
            const failCount = rs?.failedCount || 0;
            const createdCount = rs?.createdCount || 0;
            const firstError = errors.length > 0 ? ` First error: ${errors[0]}` : "";
            setAlert({
                type: failCount > 0 ? "warning" : "success",
                message: `Import done. Created: ${createdCount}, Failed: ${failCount}.${firstError}`
            });
            setShowImportModal(false);
            await fetchCardsTable(searchText);
        } catch (err) {
            setAlert({ type: "danger", message: "Import failed. Please check file format." });
        } finally {
            setIsImporting(false);
        }
    };

    const selectedFilterCount = filtersSelected?.length || 0;

    return (
        <div className="pb-4">
            <LoadingOverlay active={isLoading} />
            <ConfirmModal
                show={confirmDelete}
                title="Delete Category"
                message="Are you sure you want to delete this category?"
                onCancel={() => setConfirmDelete(false)}
                onConfirm={() => handleDeleteConfirmed()}
            />

            <AlertMessage
                type={alert.type}
                message={alert.message}
                onClose={() => setAlert({ type: "", message: "" })}
            />
            <div
                className="rounded-4 p-4 p-lg-5 mb-4 border shadow-sm"
                style={{
                    background: "linear-gradient(145deg, #f8fbff 0%, #ffffff 45%, #f3f8ff 100%)",
                    borderColor: "#e5edf8"
                }}
            >
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                    <div>
                        <h2 className="mb-1 fw-bold">Flashcard Library</h2>
                        <p className="text-secondary mb-0">
                            Build, filter, and manage your study cards in one place.
                        </p>
                    </div>
                    <div className="d-flex gap-2">
                        <button className="btn btn-outline-success rounded-pill px-3" onClick={handleOpenImportModal} title="Import from Excel/CSV">
                            <i className="bi bi-file-earmark-arrow-up me-1"></i>
                            Import CSV
                        </button>
                        <button className="btn btn-primary rounded-pill px-3" onClick={() => navigate('./add')}>
                            <i className="bi bi-plus-lg me-1"></i>
                            Add Card
                        </button>
                    </div>
                </div>
            </div>

            {/* SEARCH + FILTER */}
            <div
                className="card border-0 shadow-sm rounded-4 mb-4"
                style={{ background: "linear-gradient(180deg, #ffffff 0%, #f9fbff 100%)" }}
            >
                <div className="card-body p-4">
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                        <h6 className="fw-semibold mb-0">Search & Filters</h6>
                        <span className="badge rounded-pill text-bg-light border">
                            {selectedFilterCount} categories selected
                        </span>
                    </div>
                    <div className="row g-3">
                        <div className="col-lg-7">
                            <div className="position-relative">
                                <i
                                    className="bi bi-search position-absolute text-secondary"
                                    style={{ left: "14px", top: "50%", transform: "translateY(-50%)", zIndex: 2 }}
                                ></i>
                                <div style={{ paddingLeft: "1.85rem" }}>
                                    <SearchBox
                                        value={searchText}
                                        onChange={(v) => {
                                            setSearchText(v);
                                            setPageNumber(1);
                                        }}
                                        placeholder="Search by front text, back text, or category..."
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="col-lg-5">
                            <MultipleSearchSelectApi
                                fetchApi={handleFetchFilter}
                                value={filtersSelected}
                                onChange={(val) => {
                                    setFiltersSelected(val);
                                    setPageNumber(1); // reset page when selection changes
                                }}
                                onOk={async (selected) => {
                                    // explicit apply: selected is passed from component
                                    setFiltersSelected(selected || []);
                                    setPageNumber(1);
                                    await fetchCardsTable();
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* LIST */}
            <div className="row g-4 mb-4">
                {cards.length === 0 && (
                    <div className="col-12">
                        <div
                            className="text-center text-secondary py-5 rounded-4 border"
                            style={{ background: "#fafcff", borderColor: "#d7e4f7", borderStyle: "dashed" }}
                        >
                            <i className="bi bi-journal-x fs-2 d-block mb-2"></i>
                            No flashcards found.
                        </div>
                    </div>
                )}

                {cards.map(card => (
                    <div className="col-md-6 col-xl-4" key={card.id}>
                        <FlashcardItem
                            card={card}
                            onDelete={() => {
                                setConfirmDelete(true);
                                setIdCardToDelete(card.id);
                            }}
                            onViewDetail={(c) => setSelectedCard(c)}
                            onEdit={() => {
                                navigate(`./${card.id}`, {
                                    state: {
                                        id: card.id,
                                        categoryId: card.categoryId,
                                        frontText: card.frontText,
                                        backText: card.backText,
                                        image: card.imageUrl,
                                        fileId: card.fileId,
                                        transliteration: card.transliteration
                                    }
                                });
                            }}
                        />
                    </div>
                ))}
            </div>

            {/* PAGING */}
            <Pagination
                currentPage={pageNumber}
                totalPages={totalPages}
                onChange={setPageNumber}
            />

            {showImportModal && (
                <>
                    <div className="modal fade show d-block" tabIndex="-1" style={{ background: "rgba(8, 18, 42, 0.55)" }}>
                        <div className="modal-dialog">
                            <div className="modal-content border-0 rounded-4 overflow-hidden shadow-lg">
                                <div className="modal-header border-0 px-4 py-3" style={{ background: "linear-gradient(140deg, #ffffff 0%, #f7faff 100%)" }}>
                                    <h5 className="modal-title fw-semibold mb-0">Import Flashcards</h5>
                                    <button type="button" className="btn-close" onClick={handleCloseImportModal}></button>
                                </div>
                                <div className="modal-body px-4 py-3" style={{ background: "#fbfdff" }}>
                                    <div className="mb-3">
                                        <p className="small text-secondary mb-2">
                                            Expected columns: <strong>English text</strong>, <strong>VietNamText</strong>, <strong>Traslation</strong>.
                                            Choose one category below, all imported cards will use that category.
                                        </p>
                                        <button className="btn btn-outline-secondary btn-sm rounded-pill px-3" onClick={handleDownloadTemplate}>
                                            <i className="bi bi-download me-1"></i>
                                            Download template
                                        </button>
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label fw-medium">Category <span className="text-danger">*</span></label>
                                        <div className="position-relative" ref={importCategoryDropdownRef}>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Search category..."
                                                value={importCategorySearch}
                                                onFocus={() => setShowImportCategoryDropdown(true)}
                                                onChange={(e) => {
                                                    setImportCategorySearch(e.target.value);
                                                    setImportCategoryId("");
                                                    setShowImportCategoryDropdown(true);
                                                }}
                                                disabled={isImporting}
                                            />
                                            {showImportCategoryDropdown && (
                                                <div
                                                    className="border rounded bg-white position-absolute w-100 mt-1 shadow-sm"
                                                    style={{ maxHeight: "220px", overflowY: "auto", zIndex: 1000 }}
                                                    onScroll={(e) => {
                                                        const el = e.currentTarget;
                                                        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 16) {
                                                            loadMoreImportCategories();
                                                        }
                                                    }}
                                                >
                                                    {importCategories.map((cat) => (
                                                        <button
                                                            key={cat.value}
                                                            type="button"
                                                            className="w-100 text-start border-0 bg-white p-2 hover-bg"
                                                            onClick={() => {
                                                                setImportCategoryId(cat.value);
                                                                setImportCategorySearch(cat.label);
                                                                setShowImportCategoryDropdown(false);
                                                            }}
                                                        >
                                                            {cat.label}
                                                        </button>
                                                    ))}
                                                    {isLoadingImportCategories && (
                                                        <div className="p-2 text-muted small">Loading categories...</div>
                                                    )}
                                                    {isLoadingMoreImportCategories && (
                                                        <div className="p-2 text-muted small">Loading more...</div>
                                                    )}
                                                    {!isLoadingImportCategories && importCategories.length === 0 && (
                                                        <div className="p-2 text-muted small">No categories found</div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label fw-medium">Upload file</label>
                                        <input
                                            type="file"
                                            className="form-control form-control-lg"
                                            accept=".csv"
                                            onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                                        />
                                    </div>

                                    <div className="form-check rounded-3 border p-3" style={{ background: "#ffffff", borderColor: "#dbe8fa" }}>
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id="importIsAutogen"
                                            checked={isAutogenImport}
                                            onChange={(e) => setIsAutogenImport(e.target.checked)}
                                        />
                                        <label className="form-check-label" htmlFor="importIsAutogen">
                                            isAutogen (auto-generate voice for English text)
                                        </label>
                                    </div>
                                </div>
                                <div className="modal-footer border-0 px-4 py-3">
                                    <button className="btn btn-outline-secondary rounded-pill px-3" onClick={handleCloseImportModal} disabled={isImporting}>
                                        Cancel
                                    </button>
                                    <button
                                        className="btn btn-success rounded-pill px-3"
                                        onClick={handleImportCards}
                                        disabled={isImporting || isLoadingImportCategories || !importCategoryId || !importFile}
                                    >
                                        {isImporting ? "Importing..." : "Import"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-backdrop fade show" onClick={handleCloseImportModal}></div>
                </>
            )}
            <SidePanel
                open={!!selectedCard}
                onClose={() => setSelectedCard(null)}
                title="Flashcard detail"
            >
                {selectedCard && (
                    <>
                        <h6 className="fw-bold">{selectedCard.frontText}</h6>

                        {selectedCard.imageUrl && (
                            <img
                                src={selectedCard.imageUrl}
                                className="img-fluid rounded mb-3"
                                alt=""
                            />
                        )}

                        <p>{selectedCard.backText}</p>

                        {selectedCard.voiceUrl && (
                            <button
                                className="btn btn-outline-primary"
                                onClick={() => new Audio(selectedCard.voiceUrl).play()}
                            >
                                🔊 Play audio
                            </button>
                        )}
                    </>
                )}
            </SidePanel>
        </div>
    );
}
